import { useSpring, animated, easings } from '@react-spring/web';
import React from 'react';
import { Link } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export const Hero = () => {
	const textAnimation = useSpring({
		from: { opacity: 0, transform: 'translateY(40px)' },
		to: { opacity: 1, transform: 'translateY(0)' },
		config: { duration: 900, easing: easings.easeOutCubic },
	});

	const imageAnimation = useSpring({
		from: { opacity: 0, transform: 'translateX(60px) scale(0.97)' },
		to: { opacity: 1, transform: 'translateX(0) scale(1)' },
		config: { duration: 1100, easing: easings.easeOutCubic },
		delay: 150,
	});

	const buttonAnimation = useSpring({
		from: { opacity: 0, transform: 'translateY(20px)' },
		to: { opacity: 1, transform: 'translateY(0)' },
		config: { duration: 600, easing: easings.easeOutCubic },
		delay: 700,
	});

	const scrollToContent = () => {
		window.scrollTo({
			top: window.innerHeight - 80,
			behavior: 'smooth',
		});
	};

	return (
		<section className="relative min-h-[100svh] overflow-hidden bg-gradient-to-br from-gray-50 via-white to-primary-50">
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-100/60 rounded-full blur-3xl" />
				<div className="absolute top-1/3 -left-24 w-80 h-80 bg-accent-100/40 rounded-full blur-3xl" />
				<div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary-50/80 rounded-full blur-3xl" />
			</div>

			<div className="relative max-w-7xl mx-auto px-5 lg:px-6 pt-20 lg:pt-28 pb-10 lg:pb-20 min-h-[100svh] flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-16">
				<animated.div
					style={textAnimation}
					className="flex-1 text-center lg:text-left z-10 order-1 w-full"
				>
					<div className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-bold mb-4 lg:mb-7 font-montserrat-custom-medium tracking-widest uppercase">
						Trenerka personalna kobiet · Rzeszów
					</div>

					<h1 className="font-playfair-custom-bold text-5xl sm:text-6xl lg:text-8xl text-gray-900 mb-3 lg:mb-5 leading-none tracking-tight overflow-visible">
						Amelia
						<span className="block text-gradient pb-3">Pałczyńska</span>
					</h1>

					<p className="font-montserrat-custom-medium text-base lg:text-xl text-gray-500 mb-5 lg:mb-10 max-w-md mx-auto lg:mx-0 leading-relaxed">
						Pomogę Ci walczyć z bólem i przeprowadzę Cię przez drogę do{' '}
						<span className="text-primary-600 font-bold">wymarzonej sylwetki</span>
					</p>

					<animated.div
						style={buttonAnimation}
						className="flex flex-row gap-3 justify-center lg:justify-start flex-wrap"
					>
						<Link
							to="/contact"
							className="inline-flex items-center justify-center gap-2 px-6 lg:px-8 py-3.5 lg:py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold rounded-full shadow-soft-lg hover:shadow-card-hover hover:scale-105 transition-all duration-300 text-sm lg:text-base"
						>
							ZAPISZ SIĘ
							<ArrowForwardIcon fontSize="small" />
						</Link>
						<Link
							to="/about-me"
							className="inline-flex items-center justify-center gap-2 px-6 lg:px-8 py-3.5 lg:py-4 bg-white text-gray-700 font-bold rounded-full shadow-soft hover:shadow-soft-lg hover:scale-105 transition-all duration-300 border border-gray-200 text-sm lg:text-base"
						>
							POZNAJ MNIE
						</Link>
					</animated.div>

					<div className="mt-5 lg:mt-14 inline-flex items-center gap-5 lg:gap-8 bg-white/70 backdrop-blur-sm rounded-2xl px-5 lg:px-7 py-3 lg:py-4 shadow-soft border border-gray-100/80">
						<div className="text-center">
							<div className="text-xl lg:text-2xl font-bold text-gray-900">4+</div>
							<div className="text-xs text-gray-500 font-montserrat-custom-medium mt-0.5">lat dośw.</div>
						</div>
						<div className="w-px h-8 lg:h-10 bg-gray-200" />
						<div className="text-center">
							<div className="text-xl lg:text-2xl font-bold text-gray-900">100+</div>
							<div className="text-xs text-gray-500 font-montserrat-custom-medium mt-0.5">klientów</div>
						</div>
						<div className="w-px h-8 lg:h-10 bg-gray-200" />
						<div className="text-center">
							<div className="text-xl lg:text-2xl font-bold text-gray-900">5.0</div>
							<div className="text-xs text-gray-500 font-montserrat-custom-medium mt-0.5">Google</div>
						</div>
					</div>
				</animated.div>

				<animated.div
					style={imageAnimation}
					className="flex-1 relative z-10 order-2 flex justify-center lg:justify-end w-full"
				>
					<div className="relative w-full max-w-[260px] sm:max-w-xs lg:max-w-lg">
						<div className="absolute -inset-6 bg-gradient-to-br from-primary-300 to-primary-500 rounded-[3.5rem] transform rotate-3 opacity-15" />
						<div className="absolute -inset-3 bg-gradient-to-br from-accent-200 to-accent-400 rounded-[3rem] transform -rotate-2 opacity-20" />
						<img
							className="relative rounded-[2.5rem] shadow-soft-lg object-cover object-top w-full max-h-[42vh] lg:max-h-[75vh] lg:aspect-auto"
							src="/photo_6239.jpg"
							alt="Amelia Pałczyńska - Trenerka personalna kobiet"
						/>
						<div className="absolute -bottom-4 -left-4 lg:-left-8 bg-white rounded-2xl shadow-soft-lg p-2.5 lg:p-3.5 flex items-center gap-2 lg:gap-3">
							<div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-accent-400 to-accent-500 rounded-full flex items-center justify-center flex-shrink-0">
								<span className="text-white text-sm lg:text-base font-bold">✓</span>
							</div>
							<div>
								<div className="font-bold text-gray-800 text-xs lg:text-sm leading-tight">Certyfikowany Trener</div>
								<div className="text-xs text-gray-400">Treningi medyczne</div>
							</div>
						</div>
						<div className="absolute -top-3 -right-3 lg:-right-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-soft-lg p-2.5 lg:p-3.5">
							<div className="text-white text-center">
								<div className="font-bold text-lg lg:text-2xl leading-none">★ 5.0</div>
								<div className="text-xs text-primary-100 mt-0.5">Google</div>
							</div>
						</div>
					</div>
				</animated.div>
			</div>

			<button
				onClick={scrollToContent}
				className="absolute bottom-5 left-1/2 transform -translate-x-1/2 animate-bounce text-gray-400 hover:text-primary-500 transition-colors duration-300"
				aria-label="Przewiń w dół"
			>
				<KeyboardArrowDownIcon fontSize="large" />
			</button>
		</section>
	);
};
